package org.ttt.autogenesis.network

import kotlinx.coroutines.CancellationException
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import kotlin.math.absoluteValue
import kotlin.random.Random

/**
 * JavaScript implementation of RestRpcBridge.
 * Optimized for browser environments and KVision integration.
 */
actual object RestRpcBridge
{
    private val rpcRegistry = RpcRegistry(RpcDirection.CLIENT)
    private var client : RestRpcClient? = null
    private var currentPlayerId : String? = null
    private var currentAccelbyteId : String? = null
    private val onConnectedCallbacks = mutableListOf<() -> Unit>()

    /**
     * The AccelByte user ID the current SSE inbound is bound to, or
     * `null` for an anonymous / pre-login session. Exposed for tests so
     * they can assert idempotency without spinning up a real server.
     */
    val connectionAccelbyteId : String?
        get() = currentAccelbyteId

    actual val rpcInvoker : RpcInvoker?
        get() = client?.rpcInvoker

    actual val isConnected : Boolean
        get() = client?.isConnected() == true

    actual val isSessionReady : Boolean
        get() = client?.isSessionReady() == true

    actual fun registerHandlers(block : RpcRegistry.() -> Unit)
    {
        block(rpcRegistry)
    }

    actual suspend fun connect(
        playerId : String,
        baseUrl : String,
        autoReconnect : Boolean,
        suppressSseLogs : Boolean,
        isGuestMode : Boolean,
        accelbyteId : String?,
        origin : RpcOrigin,
        metadata : Map<String, String>,
        metadataProvider : (() -> Map<String, String>)?
    )
    {
        console.info("RestRpcBridge: connect() invoked with playerId=$playerId accelbyteId=$accelbyteId target=$baseUrl guestMode=$isGuestMode origin=$origin")
        Logger.info(
            LogCategory.NETWORK,
            "RestRpcBridgeJs.connect: playerId=$playerId accelbyteId=$accelbyteId baseUrl=$baseUrl guestMode=$isGuestMode suppressSseLogs=$suppressSseLogs origin=$origin"
        )
        // TDD-REGRESSION (fixed 2026-08-12, fourth iteration): the SKIP-LOGIN
        // boot path was firing 4-5 redundant `connect()` callbacks. The
        // root cause is that the bridge's `client` field is mutated
        // BEFORE assignments to `currentPlayerId` / `currentAccelbyteId`
        // — and the new `client = restClient` line sits AFTER the
        // `waitForConnection()` suspend point. So coroutines that run
        // during the suspend see a stale (or null) bridge state and
        // bypass the dedup.
        //
        // The fix is to assign bridge state (client / currentPlayerId /
        // currentAccelbyteId) BEFORE the suspend at waitForConnection.
        // The teardown of the previous client can happen AFTER the new
        // one is wired up — the dedup correctness depends on the bridge
        // fields being observable to the next storm coroutine, not on
        // the old SSE channel being torn down immediately.
        //
        // Flow:
        //   1. Read current state for the dedup decision.
        //   2. Run the dedup. Return early on match (no mutation).
        //   3. Build the new RestRpcClient WITHOUT touching bridge state.
        //   4. Install the new client into bridge state (client / ids).
        //   5. Suspend on waitForConnection.
        //   6. (After resume) Tear down the old client asynchronously.
        //      Old SSE channel close does not block the new one — the
        //      `disconnect()` / `close()` pair is fire-and-forget from
        //      the bridge's perspective; the orphaned old client's
        //      keepReconnecting=false prevents its auto-reconnect loop
        //      from racing the new live channel.
        val existingClient = client
        val boundAccelbyteId = currentAccelbyteId
        // Check the new accelbyteId-only dedup FIRST (the storm key).
        if (existingClient != null &&
            boundAccelbyteId != null &&
            accelbyteId != null &&
            boundAccelbyteId == accelbyteId &&
            existingClient.isConnected()) {
            console.info("RestRpcBridge: skip rebind — already bound to accelbyteId=" + accelbyteId + " (was playerId=" + currentPlayerId + ", incoming playerId=" + playerId + ")")
            Logger.info(
                LogCategory.NETWORK,
                "RestRpcBridgeJs.connect: skipping rebind — same accelbyteId=" + accelbyteId + ", current playerId=" + currentPlayerId + " is connected (incoming playerId=" + playerId + " rejected to defuse the boot reconnect storm)"
            )
            return
        }
        // Then check the legacy playerId+accelbyteId dedup (full match).
        if (currentPlayerId == playerId && currentAccelbyteId == accelbyteId && isConnected) {
            console.info("RestRpcBridge: already connected as " + playerId + " (accelbyteId=" + accelbyteId + ")")
            return
        }

        // Build the new RestRpcClient WITHOUT touching bridge state.
        val restClient = RestRpcClient(
            RestRpcClientConfig(
                baseUrl = baseUrl,
                accelbyteId = accelbyteId,
                playerId = playerId,
                suppressSseLogs = suppressSseLogs,
                isGuestMode = isGuestMode,
                origin = origin
            ),
            rpcRegistry = rpcRegistry,
            metadata = metadata,
            metadataProvider = metadataProvider ?: { metadata }
        )
        restClient.connect(autoReconnect)

        // Install the new client into bridge state BEFORE the suspend
        // at waitForConnection. This is the load-bearing fix: the next
        // storm coroutine reads `client` and sees the new value, not
        // null.
        client = restClient
        currentPlayerId = playerId
        currentAccelbyteId = accelbyteId

        check(restClient.waitForConnection()) { "SSE connection timed out - server may be unavailable" }
        console.info("RestRpcBridge: connected to $baseUrl as $playerId (accelbyteId=$accelbyteId)")

        // Fire callbacks AFTER SSE connection confirmed open and client is fully assigned.
        onConnectedCallbacks.forEach { it.invoke() }
        onConnectedCallbacks.clear()

        // Tear down the old client AFTER the new one is wired up. The
        // orphan's auto-reconnect is disabled by disconnect() so it
        // cannot race the live channel.
        val oldClient = existingClient
        if (oldClient != null) {
            try {
                oldClient.disconnect()
                oldClient.close()
            } catch (ce: kotlinx.coroutines.CancellationException) {
                throw ce
            } catch (t: Throwable) {
                Logger.warn(
                    LogCategory.NETWORK,
                    "RestRpcBridgeJs.connect: previous client close threw (non-fatal): " +
                            (t.message ?: t::class.simpleName ?: "unknown")
                )
            }
        }
    }

        /**
     * Delegate to the active [RestRpcClient]. See
     * [RestRpcClient.awaitInFlightRequests] for the full contract.
     */
    actual suspend fun awaitInFlightRequests(timeoutMillis: Long): Boolean
    {
        val c = client ?: return true
        return c.awaitInFlightRequests(timeoutMillis)
    }

actual suspend fun close()
    {
        client?.let {
            it.disconnect()
            try
            {
                it.close()
            }
            catch(err : CancellationException)
            {
                throw err
            }
            finally
            {
                client = null
                currentPlayerId = null
                currentAccelbyteId = null
            }
        }
    }

    /**
     * Register a callback to be invoked once the SSE connection is established.
     * If already connected, the callback is invoked immediately.
     * The callback is cleared after invocation.
     */
    actual fun onConnected(fn: () -> Unit)
    {
        if (client?.isConnected() == true)
        {
            fn()
        }
        else
        {
            onConnectedCallbacks.add(fn)
        }
    }

    actual fun generatePlayerId() : String =
        "${RestRpcBridgeConfig.PLAYER_ID_PREFIX}-${Random.nextInt().absoluteValue}"
}
